/**
 * Audit event types, the serialized-value format, and validators.
 * See docs/event-model.md §2–3.
 *
 * The guest emits `RawEvent`s (raw facts); the engine enriches them into
 * `AuditEvent`s (sequence number, layer, severity, tags) before posting them
 * to the UI.
 */

export const CATEGORIES = [
  "network",
  "dom",
  "storage",
  "globals",
  "probe",
  "codegen",
  "timers",
  "fingerprint",
  "evasion",
  "messaging",
  "lifecycle",
] as const;
export type Category = (typeof CATEGORIES)[number];

/** Ordered from least to most severe. */
export const SEVERITIES = ["info", "notable", "suspicious", "critical"] as const;
export type Severity = (typeof SEVERITIES)[number];

/** 0 for `info` up to 3 for `critical`. */
export const severityRank = (severity: Severity): number => SEVERITIES.indexOf(severity);

/** The more severe of two severities. */
export const maxSeverity = (a: Severity, b: Severity): Severity =>
  severityRank(b) > severityRank(a) ? b : a;

export const CONSOLE_LEVELS = ["log", "info", "warn", "error", "debug"] as const;
export type ConsoleLevel = (typeof CONSOLE_LEVELS)[number];

/**
 * A guest value in a JSON-safe tagged form, produced inside the guest without
 * invoking user getters (docs/event-model.md §3).
 */
export type SerializedValue =
  | string
  | number
  | boolean
  | null
  | { $undefined: true }
  | { $num: "NaN" | "Infinity" | "-Infinity" | "-0" }
  | { $bigint: string }
  | { $symbol: string }
  /** A string cut at the size limit; `length` is the original length. */
  | { $str: string; length: number }
  | { $fn: string }
  | { $el: string }
  | { $err: string; name?: string; stack?: string }
  | { $circular: true }
  | { $bytes: string; length: number }
  /** `length` is the original length; `$arr` may hold fewer items. */
  | { $arr: SerializedValue[]; length: number }
  /** `more` counts keys left out by the breadth limit; `ctor` names a non-plain class. */
  | { $obj: { [key: string]: SerializedValue }; more?: number; ctor?: string };

/** Where an event was caused, as `line:column` within a code layer (0 = user code). */
export interface SourceSite {
  layer: number;
  line: number;
  column: number;
}

export type NetworkKind =
  | "fetch"
  | "xhr"
  | "beacon"
  | "websocket"
  | "eventsource"
  | "resource"
  | "navigation";

export interface NetworkDetail {
  kind: NetworkKind;
  method: string;
  url: string;
  /** Whether the request was only recorded or actually sent (Live network). */
  mode: "simulated" | "live";
  /** Request headers set by the script. */
  headers?: Record<string, string>;
  /** The request body (or socket message) as text, for display and canary scanning. */
  body?: string;
}

/** A scheduled timer: its id and its effective delay in virtual ms. */
export interface TimerDetail {
  id: number;
  delay: number;
}

// ── Dynamic code (docs/event-model.md §2, docs/sandbox.md §3.6) ─────────────

/**
 * Every sink through which a string (or a URL) becomes code. The `Function`
 * family is listed by constructor; `setTimeout`/`setInterval` only count when
 * given a string; `doc-write` is `document.write`/`writeln` of a script;
 * imports are `codeload` events (ADR-0015).
 */
export const CODEGEN_SINKS = [
  "eval",
  "indirect-eval",
  "Function",
  "AsyncFunction",
  "GeneratorFunction",
  "AsyncGeneratorFunction",
  "setTimeout",
  "setInterval",
  "inline-script",
  "doc-write",
  "javascript-url",
  "static-import",
  "dynamic-import",
] as const;
export type CodegenSink = (typeof CODEGEN_SINKS)[number];

/** How a revealed code layer came to exist; names its editor tab (`layer 2 · Function`). */
export const LAYER_KINDS = [
  "eval",
  "Function",
  "timer-string",
  "inline-script",
  "javascript-url",
  "module",
] as const;
export type LayerKind = (typeof LAYER_KINDS)[number];

/** A static `import` or dynamic `import()` (a `codeload` event, ADR-0015). */
export interface ModuleLoadDetail {
  /** The specifier as written, e.g. `./lib.js` or `https://cdn.example/x.js`. */
  specifier: string;
  /** The resolved absolute URL, when the specifier resolves to one. */
  url?: string;
  /** Imported bindings: export names, `default`, or `*` for a namespace import. */
  names: string[];
  dynamic: boolean;
  /** True when a generated recording stub stood in for the real module. */
  stubbed: boolean;
}

/** What a `codegen` event adds: which sink ran, with what code, and the layer it became. */
export interface CodegenDetail {
  sink: CodegenSink;
  /** The full code string the sink received (never truncated). Absent for non-strings. */
  payload?: string;
  /** The layer the payload was instrumented as (≥ 1), if it became one. */
  newLayer?: number;
  /** Why the payload couldn't be parsed; it then ran uninstrumented. */
  parseError?: string;
  /** The rewritten direct-eval call site (`__afterimage$h(arg, siteId)`). */
  siteId?: number;
  /** Set for `static-import` / `dynamic-import`. */
  module?: ModuleLoadDetail;
}

/**
 * A revealed dynamic-code layer (0 is the user's own code and has no entry).
 * Line `n` of `code` is the line a stack frame in this layer reports as
 * `n` after layer attribution (core/stack), so click-to-source lines up.
 */
export interface Layer {
  /** ≥ 1, assigned in creation order. */
  id: number;
  /** The layer whose code created this one (0 = user code); always < `id`. */
  parent: number;
  kind: LayerKind;
  /** The decoded payload as evaluated, without the layer's line prefix. */
  code: string;
  /** `seq` of the codegen event that created it, when known. */
  createdBy?: number;
  /** Where in the parent layer it was created. */
  site?: SourceSite;
  /** Set if the payload failed to parse (and ran uninstrumented). */
  parseError?: string;
}

// ── Facts for analysis (docs/event-model.md §6–7) ───────────────────────────

/**
 * Encodings in which a canary can be found in a sink payload (ADR-0011).
 * `core/canary`'s scanner precomputes each of them from the canary's value.
 */
export const CANARY_ENCODINGS = [
  "raw",
  "uri",
  "base64",
  "base64url",
  "hex",
  "reversed",
  "json",
] as const;
export type CanaryEncoding = (typeof CANARY_ENCODINGS)[number];

/**
 * Where in a sink event a canary was found: the request URL, a header value,
 * the body (or socket message / beacon data), or the call's serialized args
 * (e.g. `postMessage` data, a DOM resource URL set through an attribute).
 */
export const CANARY_HIT_FIELDS = ["url", "header", "body", "args"] as const;
export type CanaryHitField = (typeof CANARY_HIT_FIELDS)[number];

/** A canary (honeytoken) found in a sink event's payload; set by the engine, never the guest. */
export interface CanaryHit {
  /** The `Canary.id` in the run's registry (core/canary). */
  canary: string;
  encoding: CanaryEncoding;
  field: CanaryHitField;
  /** True if only a distinctive substring matched (partial or chunked exfiltration). */
  partial?: boolean;
}

/**
 * How a global or intrinsic differs at the end of the run from the boot-time
 * snapshot (docs/sandbox.md §3 step 6, §6): `added` (a new global), `deleted`,
 * `replaced` (a different value), or `redefined` (same value, different
 * descriptor, e.g. turned into an accessor or made non-writable).
 */
export const GLOBAL_CHANGES = ["added", "deleted", "replaced", "redefined"] as const;
export type GlobalChangeKind = (typeof GLOBAL_CHANGES)[number];

/** One entry of the end-of-run global/intrinsic diff (ADR-0012). */
export interface GlobalChange {
  /** Property path from the global object, e.g. `fetch` or `XMLHttpRequest.prototype.open`. */
  path: string;
  change: GlobalChangeKind;
}

/** What the guest prelude reports through the `emit` host function. */
export interface RawEvent {
  /** Virtual milliseconds since the run started. */
  t: number;
  category: Category;
  op: string;
  /** Normalized API path, e.g. `window.fetch`. */
  target: string;
  args: SerializedValue[];
  result?: SerializedValue;
  /** The guest's captured `Error().stack`; the host resolves it to a site. */
  stack?: string;
  tags?: string[];
  network?: NetworkDetail;
  timer?: TimerDetail;
  /** The DOM node the operation was on, e.g. `input#cc-number[name=cc-number]`. */
  node?: string;
  codegen?: CodegenDetail;
  /**
   * Full sink payloads the event's recorded fields cut short (a body past the
   * recording limit, deep `postMessage` data), for the engine's canary scan
   * (docs/event-model.md §6). The engine scans and drops them; they never
   * reach the log.
   */
  scan?: ScanPart[];
}

/** Text from one part of a sink's payload, for canary scanning. */
export interface ScanPart {
  field: CanaryHitField;
  text: string;
}

/** An enriched event, as stored in the run's append-only log. */
export interface AuditEvent {
  /** Monotonic per run, starting at 0. */
  seq: number;
  t: number;
  category: Category;
  op: string;
  target: string;
  args: SerializedValue[];
  result?: SerializedValue;
  site?: SourceSite;
  /** The code layer that caused the event (0 = user code). */
  layer: number;
  severity: Severity;
  tags: string[];
  network?: NetworkDetail;
  timer?: TimerDetail;
  node?: string;
  codegen?: CodegenDetail;
  /** Canaries found in this event's payload (sink events only; set by the engine). */
  canaries?: CanaryHit[];
}

/** What the guest prelude reports through the `console` host function. */
export interface RawConsoleEntry {
  level: ConsoleLevel;
  args: SerializedValue[];
  t: number;
  stack?: string;
}

/** One `console.*` call, shown in the Output pane rather than the timeline. */
export interface ConsoleEntry {
  level: ConsoleLevel;
  args: SerializedValue[];
  t: number;
  site?: SourceSite;
}

// ── Validators ──────────────────────────────────────────────────────────────

type Fields = Record<string, unknown>;

export const isObject = (v: unknown): v is Fields =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const isString = (v: unknown): v is string => typeof v === "string";
const isCount = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 0;
const isTime = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;

export const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every(isString);

const oneOf =
  <T extends string>(values: readonly T[]) =>
  (v: unknown): v is T =>
    (values as readonly unknown[]).includes(v);

export const isCategory = oneOf(CATEGORIES);
export const isSeverity = oneOf(SEVERITIES);
const isConsoleLevel = oneOf(CONSOLE_LEVELS);
const isNumTag = oneOf(["NaN", "Infinity", "-Infinity", "-0"] as const);
const isNetworkKind = oneOf<NetworkKind>([
  "fetch",
  "xhr",
  "beacon",
  "websocket",
  "eventsource",
  "resource",
  "navigation",
]);

/** True if `v` has exactly the `required` keys plus any of the `optional` ones. */
function hasShape(v: Fields, required: readonly string[], optional: readonly string[] = []) {
  const keys = Object.keys(v);
  return (
    required.every((k) => k in v) && keys.every((k) => required.includes(k) || optional.includes(k))
  );
}

function isTagged(v: Fields): boolean {
  if ("$obj" in v) {
    return (
      hasShape(v, ["$obj"], ["more", "ctor"]) &&
      isObject(v.$obj) &&
      Object.values(v.$obj).every(isSerializedValue) &&
      (v.more === undefined || isCount(v.more)) &&
      (v.ctor === undefined || isString(v.ctor))
    );
  }
  if ("$arr" in v) {
    return (
      hasShape(v, ["$arr", "length"]) &&
      Array.isArray(v.$arr) &&
      v.$arr.every(isSerializedValue) &&
      isCount(v.length)
    );
  }
  if ("$err" in v) {
    return (
      hasShape(v, ["$err"], ["name", "stack"]) &&
      isString(v.$err) &&
      (v.name === undefined || isString(v.name)) &&
      (v.stack === undefined || isString(v.stack))
    );
  }
  if ("$str" in v) return hasShape(v, ["$str", "length"]) && isString(v.$str) && isCount(v.length);
  if ("$bytes" in v) {
    return hasShape(v, ["$bytes", "length"]) && isString(v.$bytes) && isCount(v.length);
  }
  if ("$undefined" in v) return hasShape(v, ["$undefined"]) && v.$undefined === true;
  if ("$circular" in v) return hasShape(v, ["$circular"]) && v.$circular === true;
  if ("$num" in v) return hasShape(v, ["$num"]) && isNumTag(v.$num);
  for (const tag of ["$bigint", "$symbol", "$fn", "$el"]) {
    if (tag in v) return hasShape(v, [tag]) && isString(v[tag]);
  }
  return false;
}

export function isSerializedValue(v: unknown): v is SerializedValue {
  switch (typeof v) {
    case "string":
    case "boolean":
      return true;
    case "number":
      return Number.isFinite(v);
    case "object":
      return v === null || (isObject(v) && isTagged(v));
    default:
      return false;
  }
}

const isValueArray = (v: unknown): v is SerializedValue[] =>
  Array.isArray(v) && v.every(isSerializedValue);

export const isSourceSite = (v: unknown): v is SourceSite =>
  isObject(v) && isCount(v.layer) && isCount(v.line) && isCount(v.column);

const isStringRecord = (v: unknown): v is Record<string, string> =>
  isObject(v) && Object.values(v).every(isString);

export const isNetworkDetail = (v: unknown): v is NetworkDetail =>
  isObject(v) &&
  hasShape(v, ["kind", "method", "url", "mode"], ["headers", "body"]) &&
  isNetworkKind(v.kind) &&
  isString(v.method) &&
  isString(v.url) &&
  (v.mode === "simulated" || v.mode === "live") &&
  (v.headers === undefined || isStringRecord(v.headers)) &&
  (v.body === undefined || isString(v.body));

export const isTimerDetail = (v: unknown): v is TimerDetail =>
  isObject(v) && hasShape(v, ["id", "delay"]) && isCount(v.id) && isTime(v.delay);

const optional =
  <T>(check: (v: unknown) => v is T) =>
  (v: unknown): v is T | undefined =>
    v === undefined || check(v);

const isCodegenSink = oneOf(CODEGEN_SINKS);
const isLayerKind = oneOf(LAYER_KINDS);
const isLayerId = (v: unknown): v is number => isCount(v) && v >= 1;
const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";

export const isModuleLoadDetail = (v: unknown): v is ModuleLoadDetail =>
  isObject(v) &&
  hasShape(v, ["specifier", "names", "dynamic", "stubbed"], ["url"]) &&
  isString(v.specifier) &&
  optional(isString)(v.url) &&
  isStringArray(v.names) &&
  isBoolean(v.dynamic) &&
  isBoolean(v.stubbed);

export const isCodegenDetail = (v: unknown): v is CodegenDetail =>
  isObject(v) &&
  hasShape(v, ["sink"], ["payload", "newLayer", "parseError", "siteId", "module"]) &&
  isCodegenSink(v.sink) &&
  optional(isString)(v.payload) &&
  optional(isLayerId)(v.newLayer) &&
  optional(isString)(v.parseError) &&
  optional(isCount)(v.siteId) &&
  optional(isModuleLoadDetail)(v.module);

export const isLayer = (v: unknown): v is Layer =>
  isObject(v) &&
  hasShape(v, ["id", "parent", "kind", "code"], ["createdBy", "site", "parseError"]) &&
  isLayerId(v.id) &&
  isCount(v.parent) &&
  v.parent < v.id &&
  isLayerKind(v.kind) &&
  isString(v.code) &&
  optional(isCount)(v.createdBy) &&
  optional(isSourceSite)(v.site) &&
  optional(isString)(v.parseError);

const isCanaryEncoding = oneOf(CANARY_ENCODINGS);
const isCanaryHitField = oneOf(CANARY_HIT_FIELDS);
const isGlobalChangeKind = oneOf(GLOBAL_CHANGES);

export const isScanPart = (v: unknown): v is ScanPart =>
  isObject(v) && Object.keys(v).length === 2 && isCanaryHitField(v.field) && isString(v.text);

export const isCanaryHit = (v: unknown): v is CanaryHit =>
  isObject(v) &&
  hasShape(v, ["canary", "encoding", "field"], ["partial"]) &&
  isString(v.canary) &&
  isCanaryEncoding(v.encoding) &&
  isCanaryHitField(v.field) &&
  optional(isBoolean)(v.partial);

export const isGlobalChange = (v: unknown): v is GlobalChange =>
  isObject(v) &&
  hasShape(v, ["path", "change"]) &&
  isString(v.path) &&
  v.path !== "" &&
  isGlobalChangeKind(v.change);

export const isAuditEvent = (v: unknown): v is AuditEvent =>
  isObject(v) &&
  isCount(v.seq) &&
  isTime(v.t) &&
  isCategory(v.category) &&
  isString(v.op) &&
  isString(v.target) &&
  isValueArray(v.args) &&
  optional(isSerializedValue)(v.result) &&
  optional(isSourceSite)(v.site) &&
  isCount(v.layer) &&
  isSeverity(v.severity) &&
  isStringArray(v.tags) &&
  optional(isNetworkDetail)(v.network) &&
  optional(isTimerDetail)(v.timer) &&
  optional(isString)(v.node) &&
  optional(isCodegenDetail)(v.codegen) &&
  (v.canaries === undefined || (Array.isArray(v.canaries) && v.canaries.every(isCanaryHit)));

export const isConsoleEntry = (v: unknown): v is ConsoleEntry =>
  isObject(v) &&
  isConsoleLevel(v.level) &&
  isValueArray(v.args) &&
  isTime(v.t) &&
  optional(isSourceSite)(v.site);

function parseJson(json: string): unknown {
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

/** Parses one JSON-encoded console entry from the guest; null if malformed. */
export function parseRawConsoleEntry(json: string): RawConsoleEntry | null {
  const v = parseJson(json);
  if (!isObject(v) || !isConsoleLevel(v.level) || !isValueArray(v.args) || !isTime(v.t)) {
    return null;
  }
  if (!optional(isString)(v.stack)) return null;
  const entry: RawConsoleEntry = { level: v.level, args: v.args, t: v.t };
  if (v.stack !== undefined) entry.stack = v.stack;
  return entry;
}

/**
 * Parses one JSON-encoded raw event from the guest. Returns null if it is
 * malformed (the prelude may have been tampered with), and drops any fields
 * the guest isn't allowed to set, such as `seq` or `severity`.
 */
export function parseRawEvent(json: string): RawEvent | null {
  const v = parseJson(json);
  if (
    !isObject(v) ||
    !isTime(v.t) ||
    !isCategory(v.category) ||
    !isString(v.op) ||
    !isString(v.target) ||
    !isValueArray(v.args) ||
    !optional(isSerializedValue)(v.result) ||
    !optional(isString)(v.stack) ||
    !optional(isStringArray)(v.tags) ||
    !optional(isNetworkDetail)(v.network) ||
    !optional(isTimerDetail)(v.timer) ||
    !optional(isString)(v.node) ||
    !optional(isCodegenDetail)(v.codegen) ||
    !(v.scan === undefined || (Array.isArray(v.scan) && v.scan.every(isScanPart)))
  ) {
    return null;
  }
  const event: RawEvent = {
    t: v.t,
    category: v.category,
    op: v.op,
    target: v.target,
    args: v.args,
  };
  if (v.result !== undefined) event.result = v.result;
  if (v.stack !== undefined) event.stack = v.stack;
  if (v.tags !== undefined) event.tags = v.tags;
  if (v.network !== undefined) event.network = v.network;
  if (v.timer !== undefined) event.timer = { id: v.timer.id, delay: v.timer.delay };
  if (v.node !== undefined) event.node = v.node;
  if (v.codegen !== undefined) event.codegen = v.codegen;
  if (v.scan !== undefined) event.scan = v.scan;
  return event;
}

// ── Preview ─────────────────────────────────────────────────────────────────

function preview(v: SerializedValue): string {
  if (v === null || typeof v !== "object")
    return typeof v === "string" ? JSON.stringify(v) : String(v);
  if ("$undefined" in v) return "undefined";
  if ("$num" in v) return v.$num;
  if ("$bigint" in v) return `${v.$bigint}n`;
  if ("$symbol" in v) return `Symbol(${v.$symbol})`;
  if ("$str" in v) return `${JSON.stringify(`${v.$str}…`)} (${v.length} chars)`;
  if ("$fn" in v) return `ƒ ${v.$fn || "(anonymous)"}`;
  if ("$el" in v) return `<${v.$el}>`;
  if ("$err" in v) return `${v.name ?? "Error"}: ${v.$err}`;
  if ("$circular" in v) return "[Circular]";
  if ("$bytes" in v) return `Uint8Array(${v.length})`;
  if ("$arr" in v) {
    const items = v.$arr.map(preview);
    const rest = v.length - v.$arr.length;
    if (rest > 0) items.push(`…${rest} more`);
    return `[${items.join(", ")}]`;
  }
  const entries = Object.entries(v.$obj).map(([k, value]) => `${k}: ${preview(value)}`);
  if (v.more) entries.push(`…${v.more} more`);
  const body = entries.length > 0 ? `{${entries.join(", ")}}` : "{}";
  return v.ctor ? `${v.ctor} ${body}` : body;
}

/** A one-line, human-readable rendering of a serialized value, cut at `max` chars. */
export function previewValue(v: SerializedValue, max = 200): string {
  const text = preview(v);
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
